import { vars } from "@imwebme/clay-token";
import type { ComponentPropsWithRef } from "react";
import { forwardRef } from "react";
import type { BadgeProps } from "../Badge";
import { Badge } from "../Badge";
import { Typography } from "../Typography";
import { Flex } from "../utilities";
import { headingRecipe } from "./Heading.css";
import type { HeadingProps } from "./Heading.types";

/**
 * Badge를 사용할 경우 children으로 주입해주어야 합니다.
 *
 * @example
 * <Heading>
 *    <Heading.Badge text="56" type="variant" />
 * </Heading>
 */
const Heading = forwardRef(
  (
    { size, text, subtle, align, children, className, sx, as = "div", native }: HeadingProps,
    ref: ComponentPropsWithRef<"div">["ref"],
  ) => {
    const { root, textVariant } = headingRecipe({ size });

    return (
      <Flex.Column
        as={as}
        ref={ref}
        native={native}
        className={className}
        sx={sx}
        {...root}
        {...(align === "center" && { textAlign: "center" })}
      >
        <Flex alignItems="center" textAlign="inherit" gap={vars.spacing[1]}>
          <Typography
            sx={{ flex: align === "center" ? 1 : 0 }}
            variant={textVariant}
            whiteSpace="pre"
          >
            {text}
          </Typography>

          {children}
        </Flex>

        {subtle && (
          <Typography colorToken="textSub" variant="body-medium" whiteSpace="pre">
            {subtle}
          </Typography>
        )}
      </Flex.Column>
    );
  },
);

const HeadingBadge = (props: Omit<BadgeProps, "size">) => <Badge size="default" {...props} />;

const HeadingNameSpace = Object.assign(Heading, { Badge: HeadingBadge });

export { HeadingNameSpace as Heading };
